import { useEffect, useState } from "react";
import { useAuth } from "../auth";
import { supabase } from "../lib/supabase";
import type { Profile, Role } from "../lib/types";
import { Notice, PageHeading } from "../components/Ui";

type AdminUsersResult = {
  users?: Profile[];
  error?: string;
  temporaryPassword?: string;
  updatedCount?: number;
  failedCount?: number;
};

type CreatedAccount = {
  fullName: string;
  email: string;
  temporaryPassword: string;
};

async function functionErrorMessage(error: unknown) {
  const context = (error as { context?: Response })?.context;
  if (context) {
    try {
      const payload = await context.clone().json() as { error?: string };
      if (payload.error) return payload.error;
    } catch {
      // La réponse ne contenait pas de message JSON exploitable.
    }
  }
  return error instanceof Error ? error.message : "Erreur utilisateurs";
}

export function UsersPage() {
  const { session } = useAuth();
  const [users, setUsers] = useState<Profile[]>([]);
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    role: "user" as Role,
    receiveAlerts: false,
  });
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const [createdAccount, setCreatedAccount] = useState<CreatedAccount | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resettingPasswords, setResettingPasswords] = useState(false);

  const request = async (method: "GET" | "POST" = "GET", body?: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke("admin-users", { method, body });
    const result = (data ?? {}) as AdminUsersResult;
    if (error) throw new Error(await functionErrorMessage(error));
    if (result.error) throw new Error(result.error);
    return result;
  };

  const load = async () => {
    try {
      const result = await request();
      setUsers(result.users ?? []);
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Erreur utilisateurs" });
    }
  };

  useEffect(() => {
    if (session) void load();
  }, [session]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setMessage(null);
    setCreatedAccount(null);
    setCopied(false);
    try {
      const accountName = form.fullName.trim();
      const accountEmail = form.email.trim().toLowerCase();
      const result = await request("POST", form);
      if (!result.temporaryPassword) {
        throw new Error("Le compte a été traité, mais le mot de passe temporaire n’a pas été reçu. Mettez à jour la fonction admin-users.");
      }
      setCreatedAccount({
        fullName: accountName,
        email: accountEmail,
        temporaryPassword: result.temporaryPassword,
      });
      setMessage({
        tone: "success",
        text: `Le compte de ${accountName} est actif. Copiez son mot de passe temporaire ci-dessous.`,
      });
      setForm({ fullName: "", email: "", role: "user", receiveAlerts: false });
      await load();
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Création impossible" });
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async () => {
    if (!createdAccount) return;
    try {
      await navigator.clipboard.writeText(createdAccount.temporaryPassword);
      setCopied(true);
    } catch {
      setMessage({ tone: "error", text: "Copie automatique impossible. Sélectionnez le mot de passe manuellement." });
    }
  };

  const resetUserPasswords = async () => {
    const userCount = users.filter((user) => user.role !== "admin").length;
    if (userCount === 0) {
      setMessage({ tone: "error", text: "Aucun compte utilisateur à réinitialiser." });
      return;
    }
    const confirmed = window.confirm(
      `Réinitialiser le mot de passe de ${userCount} utilisateur${userCount > 1 ? "s" : ""} ?\n\nLes administrateurs ne seront pas modifiés. Chaque utilisateur devra choisir un nouveau mot de passe à sa prochaine connexion.`,
    );
    if (!confirmed) return;

    setResettingPasswords(true);
    setMessage(null);
    setCreatedAccount(null);
    try {
      const result = await request("POST", { action: "reset-common-passwords" });
      const updatedCount = result.updatedCount ?? 0;
      const failedCount = result.failedCount ?? 0;
      setMessage({
        tone: failedCount > 0 ? "error" : "success",
        text: failedCount > 0
          ? `${updatedCount} compte(s) mis à jour, mais ${failedCount} compte(s) n’ont pas pu être modifiés.`
          : `${updatedCount} compte(s) utilisateur ont reçu le mot de passe provisoire commun. Les administrateurs n’ont pas été modifiés.`,
      });
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Réinitialisation impossible" });
    } finally {
      setResettingPasswords(false);
    }
  };

  return (
    <>
      <PageHeading
        eyebrow="Administration"
        title="Utilisateurs"
        description="Les comptes utilisateurs reçoivent un mot de passe provisoire commun et doivent le remplacer à leur première connexion."
        action={<button className="secondary-button" type="button" onClick={resetUserPasswords} disabled={resettingPasswords || busy}>{resettingPasswords ? "Réinitialisation…" : "Réinitialiser les mots de passe"}</button>}
      />
      {message && <Notice tone={message.tone}>{message.text}</Notice>}
      {createdAccount && (
        <section className="temporary-account-card" aria-live="polite">
          <div>
            <p className="eyebrow">Compte créé</p>
            <h2>Mot de passe temporaire</h2>
            <p><strong>{createdAccount.fullName}</strong> · {createdAccount.email}</p>
          </div>
          <div className="temporary-password-row">
            <code>{createdAccount.temporaryPassword}</code>
            <button className="secondary-button" type="button" onClick={copyPassword}>
              {copied ? "Copié !" : "Copier le mot de passe"}
            </button>
          </div>
          <small>Transmettez-le directement à l’utilisateur. À sa première connexion, il devra choisir son propre mot de passe.</small>
        </section>
      )}
      <div className="admin-grid">
        <section className="form-card">
          <div className="section-title">
            <div><p className="eyebrow">Nouveau compte</p><h2>Créer un utilisateur</h2></div>
          </div>
          <form className="checkout-form" onSubmit={create}>
            <label>
              <span>Nom complet</span>
              <input value={form.fullName} onChange={(event) => setForm({ ...form, fullName: event.target.value })} required />
            </label>
            <label>
              <span>Adresse e-mail professionnelle</span>
              <input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required />
            </label>
            <label>
              <span>Rôle</span>
              <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value as Role })}>
                <option value="user">Utilisateur</option>
                <option value="admin">Administrateur</option>
              </select>
            </label>
            <label className="check-row">
              <input type="checkbox" checked={form.receiveAlerts} onChange={(event) => setForm({ ...form, receiveAlerts: event.target.checked })} />
              <span>Recevoir les alertes de stock par e-mail</span>
            </label>
            <button className="primary-button" disabled={busy}>{busy ? "Création…" : "Créer le compte"}</button>
          </form>
        </section>
        <section className="table-card">
          <div className="table-title">
            <strong>{users.length} utilisateur{users.length > 1 ? "s" : ""}</strong>
            <span>Comptes autorisés</span>
          </div>
          <div className="user-list">
            {users.map((user) => (
              <article key={user.id}>
                <div><strong>{user.full_name}</strong><span>{user.email}</span></div>
                <span className={`usage-badge ${user.role === "admin" ? "fixed" : "temporary"}`}>
                  {user.role === "admin" ? "Administrateur" : "Utilisateur"}
                </span>
                <small>{user.receive_stock_alerts ? "Alertes e-mail activées" : "Sans alertes e-mail"}</small>
              </article>
            ))}
          </div>
        </section>
      </div>
    </>
  );
}
